import * as React from 'react';
import { DragElementWrapper, DragSourceOptions, DropTarget, DragSource, DropTargetMonitor } from 'react-dnd';
import { Card, Space, Spin, Button, Checkbox, Input } from 'antd';
import { DeleteOutlined, DragOutlined } from '@ant-design/icons';
import MLModel from '../../../ml_models/models/MLModel';
import { PromptTemplate } from '../../../prompt_templates/types';
import { ApplicationDefinitionBase } from '../../../iota_applications/types/ApplicationDefinition';
import { Script } from '../../../script_management/types';
import { PipelineStep, PipelineStepFieldValue, PipelineStepType } from './CustomBlock';
import { SelectWhenScroll } from '../SelectWhenScroll';
import ClientTaxonomyStepConfig from './ClientTaxonomyStepConfig';

const ITEM_TYPE = 'PIPELINE_STEP';

interface PipelineStepCardProps {
    step: PipelineStep;
    stepIndex: number;
    models: MLModel[];
    promptTemplates: PromptTemplate[];
    applicationDefinitions: ApplicationDefinitionBase[];
    scripts: Script[];
    isLoadingModels: boolean;
    isLoadingPrompts: boolean;
    isLoadingApplications: boolean;
    isLoadingScripts: boolean;
    disabled: boolean;
    onRemove: () => void;
    onTypeChange: (newType: PipelineStepType) => void;
    onFieldChange: (field: string, value: PipelineStepFieldValue) => void;
    moveStep: (dragIndex: number, hoverIndex: number) => void;
    isOver?: boolean;
    connectDragSource?: DragElementWrapper<DragSourceOptions>;
    connectDropTarget?: DragElementWrapper<unknown>;
}

const PIPELINE_STEP_TYPES = [
    { label: 'Image', value: 'image' },
    { label: 'Text', value: 'text' },
    { label: 'Remote', value: 'remote' },
    { label: 'ClientTaxonomy', value: 'client_taxonomy' }
];

const stepSource = {
    canDrag(props: PipelineStepCardProps) {
        return !props.disabled;
    },
    beginDrag(props: PipelineStepCardProps) {
        return {
            index: props.stepIndex
        };
    }
};

const stepTarget = {
    drop(props: PipelineStepCardProps, monitor: DropTargetMonitor) {
        const dragIndex = monitor.getItem().index;
        const hoverIndex = props.stepIndex;

        if (dragIndex === hoverIndex) {
            return;
        }

        props.moveStep(dragIndex, hoverIndex);
        monitor.getItem().index = hoverIndex;
    }
};

const PipelineStepCardComponent: React.FC<PipelineStepCardProps> = ({
    step,
    stepIndex,
    models,
    promptTemplates,
    applicationDefinitions,
    scripts,
    isLoadingModels,
    isLoadingPrompts,
    isLoadingApplications,
    isLoadingScripts,
    disabled,
    onRemove,
    onTypeChange,
    onFieldChange,
    isOver,
    connectDragSource,
    connectDropTarget
}) => {
    const modelOptions = React.useMemo(() => {
        return models.map(model => ({
            label: model.name,
            value: model.id || ''
        }));
    }, [models]);

    const promptOptions = React.useMemo(() => {
        return promptTemplates.map(template => ({
            label: template.title,
            value: template.id
        }));
    }, [promptTemplates]);

    let className = 'pipeline-step-card';
    if (isOver) {
        className += ' drop-over';
    }

    const cardContent = (
        <Card size="small" className={className} style={{ backgroundColor: '#fafafa' }}>
            <div className="pipeline-step-content">
                <div className="pipeline-step-header">
                    {connectDragSource &&
                        connectDragSource(
                            <div className="drag-handle">
                                <DragOutlined />
                            </div>
                        )}
                    <span className="step-number">Step {stepIndex + 1}</span>
                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={onRemove}
                        disabled={disabled}
                        className="delete-button"
                    />
                </div>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div className="custom-block-field">
                        <label className="custom-block-label">Step Type</label>
                        <SelectWhenScroll
                            value={step.type}
                            onChange={val => onTypeChange(val)}
                            options={PIPELINE_STEP_TYPES}
                            disabled={disabled}
                            style={{ width: '100%' }}
                        />
                    </div>
                    {(step.type === 'image' || step.type === 'text') && (
                        <>
                            <div className="custom-block-field">
                                <label className="custom-block-label">Model</label>
                                <SelectWhenScroll
                                    value={step.model_id}
                                    onChange={val => onFieldChange('model_id', val)}
                                    options={modelOptions}
                                    disabled={disabled || isLoadingModels}
                                    loading={isLoadingModels}
                                    notFoundContent={isLoadingModels ? <Spin size="small" /> : 'No models found'}
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Select a model"
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className="custom-block-field">
                                <label className="custom-block-label">System Prompt</label>
                                <SelectWhenScroll
                                    value={step.system_prompt_id}
                                    onChange={val => onFieldChange('system_prompt_id', val)}
                                    options={promptOptions}
                                    disabled={disabled || isLoadingPrompts}
                                    loading={isLoadingPrompts}
                                    notFoundContent={isLoadingPrompts ? <Spin size="small" /> : 'No prompts found'}
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Select a system prompt"
                                    allowClear
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className="custom-block-field">
                                <label className="custom-block-label">Prompt</label>
                                <SelectWhenScroll
                                    value={step.prompt_id}
                                    onChange={val => onFieldChange('prompt_id', val)}
                                    options={promptOptions}
                                    disabled={disabled || isLoadingPrompts}
                                    loading={isLoadingPrompts}
                                    notFoundContent={isLoadingPrompts ? <Spin size="small" /> : 'No prompts found'}
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Select a prompt"
                                    allowClear
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}
                    {step.type === 'remote' && (
                        <div className="custom-block-field">
                            <label className="custom-block-label">Url</label>
                            <Input
                                value={step.url || ''}
                                onChange={e => onFieldChange('url', e.target.value)}
                                disabled={disabled}
                            />
                        </div>
                    )}

                    {step.type === 'client_taxonomy' && (
                        <ClientTaxonomyStepConfig
                            step={step}
                            applicationDefinitions={applicationDefinitions}
                            scripts={scripts}
                            isLoadingApplications={isLoadingApplications}
                            isLoadingScripts={isLoadingScripts}
                            disabled={disabled}
                            onFieldChange={onFieldChange}
                        />
                    )}

                    {step.type === 'image' && (
                        <div className="custom-block-field">
                            <Checkbox
                                checked={step.enable_preprocessing || false}
                                onChange={e => onFieldChange('enable_preprocessing', e.target.checked)}
                                disabled={disabled}
                            >
                                Enable Preprocessing
                            </Checkbox>
                        </div>
                    )}
                </Space>
            </div>
        </Card>
    );

    if (connectDropTarget) {
        return connectDropTarget(<div>{cardContent}</div>);
    }

    return cardContent;
};

export const PipelineStepCard = DropTarget(ITEM_TYPE, stepTarget, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver()
}))(
    DragSource(ITEM_TYPE, stepSource, connect => ({
        connectDragSource: connect.dragSource()
    }))(PipelineStepCardComponent)
);
